import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

export interface GraphNode {
  id: string;
  type: "weather" | "energy" | "soil" | "climate" | "tabular";
  properties: Record<string, any>;
  timestamp: Date;
  location?: { lat: number; lng: number };
}

export interface GraphRelationship {
  id: string;
  sourceId: string;
  targetId: string;
  type: "affects" | "influences" | "correlates_with" | "predicts" | "optimizes";
  properties: Record<string, any>;
  confidence: number;
  timestamp: Date;
}

export interface PatternResult {
  patternId: string;
  type: "correlation" | "anomaly" | "trend" | "prediction";
  description: string;
  confidence: number;
  nodes: string[];
  relationships: string[];
  metadata: Record<string, any>;
  timestamp: Date;
}

export interface KnowledgeGraphState {
  nodes: GraphNode[];
  relationships: GraphRelationship[];
  patterns: PatternResult[];
  loading: boolean;
  error: string | null;
  selectedNode: GraphNode | null;
  graphView: "nodes" | "relationships" | "patterns";
}

const initialState: KnowledgeGraphState = {
  nodes: [],
  relationships: [],
  patterns: [],
  loading: false,
  error: null,
  selectedNode: null,
  graphView: "nodes",
};

// Async thunks
export const fetchNodesByType = createAsyncThunk(
  "knowledgeGraph/fetchNodesByType",
  async (type: string) => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/knowledge-graph/nodes/${type}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      },
    );
    if (!response.ok) {
      throw new Error("Failed to fetch nodes");
    }
    return response.json();
  },
);

export const createNode = createAsyncThunk(
  "knowledgeGraph/createNode",
  async (nodeData: Omit<GraphNode, "id" | "timestamp">) => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/knowledge-graph/nodes`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(nodeData),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to create node");
    }
    return response.json();
  },
);

export const detectCorrelations = createAsyncThunk(
  "knowledgeGraph/detectCorrelations",
  async () => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/knowledge-graph/patterns/correlation`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      },
    );
    if (!response.ok) {
      throw new Error("Failed to detect correlations");
    }
    return response.json();
  },
);

export const detectAnomalies = createAsyncThunk(
  "knowledgeGraph/detectAnomalies",
  async () => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/knowledge-graph/patterns/anomalies`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      },
    );
    if (!response.ok) {
      throw new Error("Failed to detect anomalies");
    }
    return response.json();
  },
);

export const predictEnergyOutput = createAsyncThunk(
  "knowledgeGraph/predictEnergyOutput",
  async (weatherData: any) => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/knowledge-graph/predict/energy`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(weatherData),
      },
    );
    if (!response.ok) {
      throw new Error("Failed to predict energy output");
    }
    return response.json();
  },
);

const knowledgeGraphSlice = createSlice({
  name: "knowledgeGraph",
  initialState,
  reducers: {
    setSelectedNode: (state, action: PayloadAction<GraphNode | null>) => {
      state.selectedNode = action.payload;
    },
    setGraphView: (
      state,
      action: PayloadAction<"nodes" | "relationships" | "patterns">,
    ) => {
      state.graphView = action.payload;
    },
    addNode: (state, action: PayloadAction<GraphNode>) => {
      state.nodes.push(action.payload);
    },
    addRelationship: (state, action: PayloadAction<GraphRelationship>) => {
      state.relationships.push(action.payload);
    },
    addPattern: (state, action: PayloadAction<PatternResult>) => {
      state.patterns.push(action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    clearGraph: (state) => {
      state.nodes = [];
      state.relationships = [];
      state.patterns = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch nodes by type
      .addCase(fetchNodesByType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNodesByType.fulfilled, (state, action) => {
        state.loading = false;
        state.nodes = action.payload;
      })
      .addCase(fetchNodesByType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch nodes";
      })
      // Create node
      .addCase(createNode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNode.fulfilled, (state, action) => {
        state.loading = false;
        state.nodes.push(action.payload);
      })
      .addCase(createNode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create node";
      })
      // Detect correlations
      .addCase(detectCorrelations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(detectCorrelations.fulfilled, (state, action) => {
        state.loading = false;
        state.patterns = action.payload;
      })
      .addCase(detectCorrelations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to detect correlations";
      })
      // Detect anomalies
      .addCase(detectAnomalies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(detectAnomalies.fulfilled, (state, action) => {
        state.loading = false;
        state.patterns = action.payload;
      })
      .addCase(detectAnomalies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to detect anomalies";
      })
      // Predict energy output
      .addCase(predictEnergyOutput.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(predictEnergyOutput.fulfilled, (state, action) => {
        state.loading = false;
        state.patterns.push(action.payload);
      })
      .addCase(predictEnergyOutput.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to predict energy output";
      });
  },
});

export const {
  setSelectedNode,
  setGraphView,
  addNode,
  addRelationship,
  addPattern,
  clearError,
  clearGraph,
} = knowledgeGraphSlice.actions;

export default knowledgeGraphSlice.reducer;
